import React, { useState, useEffect } from 'react';
import { formatMoney } from 'accounting-js';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/nextjs';

import Link from '../../utils/link';
import Typography from '../../atoms/typography';
import PromoMessaging from '../promo-messaging';

import { useProduct } from '../../utils/context/product-provider';
import getPromoMessages from '../../utils/getPromoMessages';
import theme from '../../theme';

const Brand = styled(Typography)`
  color: ${theme.color.bluePrimary};
  cursor: pointer;
  margin-bottom: 0.2em;
  text-transform: uppercase;
  line-height: ${theme.modularScale.sixteen};
  letter-spacing: .1rem;
  margin-bottom: ${theme.modularScale.eight};
`;

const Title = styled(Typography)`
  color: ${theme.color.bluePrimary};
  line-height: ${(props) => props.theme.modularScale.thirtyTwo};
  text-transform: capitalize;

  @media (min-width: ${theme.breakpoint.medium}){
    font-size: ${(props) => props.theme.modularScale.thirtyTwo};
    line-height: 4rem;
}
`;

const Price = styled(Typography)`
  color: ${theme.color.bluePrimary};
  justify-content: left;
  letter-spacing: normal;
  line-height: ${theme.modularScale.twentyFour};
  margin-top: 1rem;
  text-transform: lowercase;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  white-space: pre-wrap;
`;

const FinalSale = styled(Typography)`
  color: ${(props) => props.theme.color.brandLightBlue};
  font-weight: 400;
  text-transform: capitalize;
  white-space: nowrap;
  `;

const NoUnderlineAnchor = styled.a`
  text-decoration: none;
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-content: center;
  margin-bottom: 2.4rem;

  ${Brand}, ${Title}, ${Price} {
    text-align: left;
  }
`;

const OptionInvalid = styled.span`
  color: ${(props) => props.theme.color.brandError};
`;

const MobilePaidProductHeader = (props) => {
  const { state: { addedOptionalMonogram } } = useProduct();

  const getVendorPrices = () => props?.variant?.prices.filter(
    (v) => v.vendor_id === props.vendor
  ) ?? [];

  const getPrice = () => {
    const priceArray = props?.variant ? getVendorPrices() : props.prices;

    const prices = priceArray.map(
      (p) => ({ original_price: p?.original_price ?? 0.0, price: p?.price ?? 0.0 })
    );
    const sales = priceArray.filter((p) => p?.on_sale || false);

    const final = { has_sale: false, discount: null };

    const getMin = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price < +a.price && +p.price > 0) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, {
      original_price: Math.min.apply(null, arr.map((p) => p.original_price).filter(Boolean)),
      price: Math.min.apply(null, arr.map((p) => p.price).filter(Boolean))
    });

    const getMax = (arr) => arr.reduce((acc, p) => {
      const a = acc;

      if (+p.price > +a.price) {
        a.original_price = +p.original_price;
        a.price = +p.price;
      }

      return a;
    }, { original_price: +arr[0].original_price, price: +arr[0].price });

    if (sales.length > 0) {
      const percents = sales.map((s) => Math.round(s.discount_percent));

      if (percents.every((p) => p === percents[0])) {
        final.discount = `${Math.round(percents[0])}% Off`;
      } else {
        final.discount = `Up to ${Math.max(...percents)}% Off`;
      }

      final.has_sale = true;
      final.max = getMax(sales);
      final.min = getMin(sales);
    } else {
      final.min = prices.length ? getMin(prices) : { price: 0 };
      final.max = prices.length ? getMax(prices) : { price: 0 };
    }

    if (props.variant && props.variant?.prices[0]?.monogram) {
      const { monogram } = props.variant?.prices[0];

      // if the current variant has
      // optional monogram with
      // an additional cost
      if (monogram.monogrammable
        && !monogram.monogrammable_only) {
        final.monogram_price = +monogram.monogram_price;
      }
    }

    // This checks for whether any price is NaN and logs message to Sentry
    const priceIsNaN = (price) => Number.isNaN(Number.parseInt(price, 10));
    const priceValues = [...(Object.values(final.max)), ...(Object.values(final.min))];

    // TODO: Remove this Sentry check when the bug is confirmed fixed
    if (priceValues?.length && priceValues.some(priceIsNaN)) {
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setFingerprint(['{{default}}', 'PriceIsNaN.Undefined']);
        Sentry.captureMessage('[TEC-6863] NaN price on PDP');
      });
    }
    return final;
  };

  const [currentPrice, setCurrentPrice] = useState(getPrice());

  const addMonogramPrice = (priceObj, monogramCost) => {
    const final = {};

    final.original_price = priceObj.original_price + monogramCost;
    final.price = priceObj.original_price + monogramCost;

    return final;
  };

  useEffect(() => {
    // if user had selected the optional monogram
    // but switched sizes, adjust pricing accordingly if
    // there is a discrepancy between the different size pricings
    if (props.variant && addedOptionalMonogram) {
      const newPrice = getPrice();
      const { max, monogram_price } = newPrice;

      if ((max.original_price + monogram_price) !== currentPrice.max.original_price) {
        newPrice.max = addMonogramPrice(newPrice.max, newPrice.monogram_price);
        newPrice.min = addMonogramPrice(newPrice.min, newPrice.monogram_price);

        setCurrentPrice(newPrice);
      }
    } else {
      setCurrentPrice(getPrice());
    }
  }, [props.variant]);

  useEffect(() => {
    if (addedOptionalMonogram && currentPrice.monogram_price) {
      const withMonogramPrice = currentPrice;

      withMonogramPrice.max = addMonogramPrice(currentPrice.max, currentPrice.monogram_price);
      withMonogramPrice.min = addMonogramPrice(currentPrice.min, currentPrice.monogram_price);

      setCurrentPrice(withMonogramPrice);
    } else {
      setCurrentPrice(getPrice());
    }
  }, [addedOptionalMonogram]);

  const [activePromoMessage] = useState(getPromoMessages(props.product?.advertised_promotions
    ?? []));

  return (
    <Header data-vwo-id="MobilePaidProductDetailHeader">
      {
        props.brand && (
          <>
            {
              props.brandSlug ? (
                <Link
                  href={`/brands/${props.brandSlug}`}
                  passHref
                >
                  <NoUnderlineAnchor data-vwo-id="MobilePaidHeaderBrandLink">
                    <Brand element="h2" like="dec-4">{props.brand}</Brand>
                  </NoUnderlineAnchor>
                </Link>
              ) : (
                <Brand element="h2" like="dec-4">{props.brand}</Brand>
              )
            }
          </>
        )
      }

      <Link
        href={`/product/${props.slug}`}
        passHref
      >
        <NoUnderlineAnchor data-vwo-id="MobilePaidHeaderTitleLink">
          <Title element="h1" like="heading-8">{props.title}</Title>
        </NoUnderlineAnchor>
      </Link>

      <Price
        tabIndex="0"
        element="div"
        like="label-3"
        data-vwo-id="MobilePaidHeaderPriceWrapper"
      >
        {
          props.noOffers || currentPrice.min.price + currentPrice.max.price === 0
            ? <OptionInvalid>Out of Stock</OptionInvalid>
            : (
              <>
                {currentPrice.has_sale && (
                  <>
                    {currentPrice.min.price !== currentPrice.max.price && 'From '}
                    <Price
                      element="span"
                      like="label-3"
                      css={() => ({
                        textDecoration: 'line-through',
                        color: theme.color.brandLightBlue,
                        marginTop: 0,
                        marginRight: '0.4rem'
                      })}
                    >
                      {formatMoney(currentPrice.min.original_price)}

                    </Price>
                    <Price
                      element="span"
                      like="label-3"
                      css={() => ({
                        marginTop: 0,
                        marginRight: '0.4rem'
                      })}
                    >
                      {formatMoney(currentPrice.min.price)}
                    </Price>
                    <Price
                      element="span"
                      like="label-3"
                      css={() => ({
                        color: theme.color.redError,
                        marginTop: 0
                      })}
                    >
                      {`(${currentPrice.discount})`}
                    </Price>
                  </>
                )}

                {!currentPrice.has_sale && (
                  <>
                    {
                      +currentPrice.min.price === +currentPrice.max.price
                        ? formatMoney(currentPrice.min.price)
                        : `${formatMoney(currentPrice.min.price)} - ${formatMoney(currentPrice.max.price)}`
                    }
                  </>
                )}
              </>
            )
        }
      </Price>

      {
          (props.finalSale || props.isGiftCard)
          && (
            <FinalSale
              element="span"
              like="dec-5"
              data-vwo-id="MobilePaidHeaderFinalSale"
            >
              Final sale
            </FinalSale>
          )
        }

      {
        activePromoMessage && activePromoMessage.length > 0
          ? activePromoMessage.map(
            (activePromoObject) =>
            (
              <PromoMessaging
                key={activePromoObject.id}
                pdpText={activePromoObject?.advertised_text}
              />
            )
          ) : null
      }
    </Header>
  );
};

MobilePaidProductHeader.defaultProps = {
  title: '',
  brand: '',
  brandSlug: '',
  slug: '',
  variant: false,
  vendor: 0,
  noOffers: false,
  finalSale: false,
  isGiftCard: false
};

MobilePaidProductHeader.propTypes = {
  product: PropTypes.object.isRequired,
  brand: PropTypes.string,
  brandSlug: PropTypes.string,
  slug: PropTypes.string,
  title: PropTypes.string,
  prices: PropTypes.array.isRequired,
  variant: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  vendor: PropTypes.number,
  noOffers: PropTypes.bool,
  finalSale: PropTypes.bool,
  isGiftCard: PropTypes.bool
};

MobilePaidProductHeader.whyDidYouRender = true;

export default MobilePaidProductHeader;
